<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import TalkviaForumAPI from 'dashboard/api/talkviaForum';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ForumComposerExtras from './ForumComposerExtras.vue';

const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const loading = ref(true);
const savingReply = ref(false);
const interactionId = ref(null);
const topic = ref(null);
const replyBody = ref('');
const replyFiles = ref([]);
const replyMentionedUsers = ref([]);
const replySort = ref('relevant');
const replyComposer = ref(null);
const replyComposerCollapsed = ref(false);
const replyComposerFocused = ref(false);
let lastPageScrollTop = 0;
const reportTarget = ref(null);
const reportForm = reactive({ reason: 'spam', details: '' });
const reportReasons = [
  { value: 'spam', label: 'Spam ou propaganda' },
  { value: 'abuse', label: 'Conteúdo abusivo' },
  { value: 'harassment', label: 'Assédio' },
  { value: 'misinformation', label: 'Informação enganosa' },
  { value: 'privacy', label: 'Exposição de dados pessoais' },
  { value: 'other', label: 'Outro motivo' },
];

const canParticipate = computed(
  () => topic.value?.membership?.can_participate !== false
);
const replySortOptions = [
  { value: 'relevant', label: 'Mais relevantes' },
  { value: 'recent', label: 'Mais recentes' },
  { value: 'oldest', label: 'Mais antigas' },
];
const sortedPosts = computed(() => {
  const posts = [...(topic.value?.posts || [])];
  return posts.sort((first, second) => {
    if (first.id === topic.value.accepted_post_id) return -1;
    if (second.id === topic.value.accepted_post_id) return 1;
    if (replySort.value === 'relevant') {
      return (
        second.likes_count - first.likes_count ||
        new Date(first.created_at) - new Date(second.created_at)
      );
    }
    const direction = replySort.value === 'recent' ? -1 : 1;
    return (
      direction * (new Date(first.created_at) - new Date(second.created_at))
    );
  });
});
const loadTopic = async () => {
  loading.value = true;
  try {
    const { data } = await TalkviaForumAPI.topic(route.params.topicId);
    topic.value = data;
  } catch (error) {
    useAlert('O tópico não foi encontrado ou não está mais disponível.');
    router.push(accountScopedRoute('talkvia_forum'));
  } finally {
    loading.value = false;
  }
};

const createReply = async () => {
  if (!replyBody.value.trim()) return;
  savingReply.value = true;
  try {
    const { data } = await TalkviaForumAPI.createPost(
      topic.value.id,
      replyBody.value,
      replyFiles.value,
      replyMentionedUsers.value.map(user => user.id)
    );
    topic.value.posts.push(data);
    topic.value.posts_count += 1;
    replyBody.value = '';
    replyFiles.value = [];
    replyMentionedUsers.value = [];
    replyComposerCollapsed.value = true;
    useAlert('Resposta publicada.');
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível publicar a resposta.'
    );
  } finally {
    savingReply.value = false;
  }
};

const toggleLike = async post => {
  interactionId.value = `like-${post.id}`;
  try {
    const { data } = await TalkviaForumAPI.setLike(post.id, post.liked);
    Object.assign(post, data);
  } catch (error) {
    useAlert('Não foi possível atualizar a curtida.');
  } finally {
    interactionId.value = null;
  }
};

const toggleSubscription = async () => {
  interactionId.value = 'subscription';
  try {
    await TalkviaForumAPI.setSubscription(
      topic.value.id,
      topic.value.subscribed
    );
    topic.value.subscribed = !topic.value.subscribed;
    useAlert(
      topic.value.subscribed
        ? 'Você está acompanhando este tópico.'
        : 'Acompanhamento removido.'
    );
  } catch (error) {
    useAlert('Não foi possível atualizar o acompanhamento.');
  } finally {
    interactionId.value = null;
  }
};

const acceptSolution = async post => {
  interactionId.value = `solution-${post.id}`;
  try {
    const { data } = await TalkviaForumAPI.acceptSolution(
      topic.value.id,
      post.id
    );
    topic.value = data;
    useAlert('Resposta marcada como solução.');
  } catch (error) {
    useAlert('Não foi possível marcar esta resposta como solução.');
  } finally {
    interactionId.value = null;
  }
};

const openReport = (type, record) => {
  reportTarget.value = { type, record };
  reportForm.reason = 'spam';
  reportForm.details = '';
};

const submitReport = async () => {
  interactionId.value = 'report';
  try {
    await TalkviaForumAPI.report({
      reportable_type: reportTarget.value.type,
      reportable_id: reportTarget.value.record.id,
      ...reportForm,
    });
    reportTarget.value = null;
    useAlert('Denúncia enviada para a equipe de moderação.');
  } catch (error) {
    useAlert(
      error.response?.status === 422
        ? 'Você já denunciou este conteúdo.'
        : 'Não foi possível enviar a denúncia.'
    );
  } finally {
    interactionId.value = null;
  }
};

const formatDate = value =>
  new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
const roleLabel = role =>
  ({
    member: 'Membro',
    contributor: 'Colaborador',
    moderator: 'Moderador',
    administrator: 'Administrador',
  })[role] || 'Membro';

const handlePageScroll = event => {
  const scrollTop = event.currentTarget.scrollTop;
  const scrollingDown = scrollTop > lastPageScrollTop + 12;
  const hasDraft =
    replyBody.value.trim().length > 0 ||
    replyFiles.value.length > 0 ||
    replyMentionedUsers.value.length > 0;

  if (
    scrollingDown &&
    scrollTop > 96 &&
    !replyComposerFocused.value &&
    !hasDraft
  ) {
    replyComposerCollapsed.value = true;
  }
  lastPageScrollTop = scrollTop;
};

const expandReplyComposer = async () => {
  replyComposerCollapsed.value = false;
  await nextTick();
  replyComposer.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
};

const handleComposerFocusOut = event => {
  replyComposerFocused.value = event.currentTarget.contains(
    event.relatedTarget
  );
};

onMounted(loadTopic);
</script>

<template>
  <main
    class="mx-auto flex h-full w-full max-w-6xl min-w-0 flex-col gap-5 overflow-y-auto p-4 sm:p-6"
    @scroll.passive="handlePageScroll"
  >
    <Button
      label="Voltar à comunidade"
      icon="i-lucide-arrow-left"
      color="slate"
      variant="link"
      class="self-start"
      @click="router.push(accountScopedRoute('talkvia_forum'))"
    />
    <div v-if="loading" class="flex min-h-64 items-center justify-center">
      <Icon icon="i-lucide-loader-2" class="size-7 animate-spin text-n-brand" />
    </div>
    <template v-else-if="topic">
      <article
        class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 shadow-sm sm:p-6"
      >
        <div class="flex flex-wrap items-center gap-2">
          <span
            v-if="topic.pinned"
            class="rounded-full bg-n-violet-3 px-2 py-1 text-xs font-medium text-n-violet-11"
            >Fixado</span
          ><span
            v-if="topic.solved"
            class="rounded-full bg-n-teal-3 px-2 py-1 text-xs font-medium text-n-teal-11"
            >Resolvido</span
          ><span
            class="rounded-full border border-n-weak px-2 py-1 text-xs text-n-slate-10"
            >{{ topic.category.name }}</span
          >
        </div>
        <h1 class="mt-4 text-2xl font-semibold leading-tight text-n-slate-12">
          {{ topic.title }}
        </h1>
        <div class="mt-4 flex items-center gap-3">
          <img
            v-if="topic.author.avatar_url"
            :src="topic.author.avatar_url"
            :alt="topic.author.name"
            class="size-10 rounded-full object-cover"
          /><span
            v-else
            class="grid size-10 place-items-center rounded-full bg-n-violet-3 font-semibold text-n-violet-11"
            >{{ topic.author.name.charAt(0) }}</span
          >
          <div>
            <p class="text-sm font-semibold text-n-slate-12">
              {{ topic.author.name }}
              <span
                v-if="topic.author.role !== 'member'"
                class="ml-1 text-xs font-medium text-n-violet-10"
                >{{ roleLabel(topic.author.role) }}</span
              >
            </p>
            <p class="text-xs text-n-slate-9">
              {{ topic.author.title || formatDate(topic.created_at) }}
            </p>
          </div>
        </div>
        <p
          class="mt-6 whitespace-pre-wrap break-words text-sm leading-7 text-n-slate-11"
        >
          {{ topic.body }}
        </p>
        <div v-if="topic.mentions?.length" class="mt-4 flex flex-wrap gap-1.5">
          <span
            v-for="user in topic.mentions"
            :key="user.id"
            class="rounded-full bg-n-violet-3 px-2.5 py-1 text-xs font-medium text-n-violet-11"
          >
            @{{ user.name }}
          </span>
        </div>
        <div
          v-if="topic.attachments?.length"
          class="mt-4 grid gap-2 sm:grid-cols-2"
        >
          <a
            v-for="attachment in topic.attachments"
            :key="attachment.id"
            :href="attachment.url"
            class="group flex min-w-0 items-center gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3 hover:bg-n-alpha-2"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              v-if="attachment.content_type.startsWith('image/')"
              :src="attachment.preview_url"
              :alt="attachment.filename"
              class="size-12 rounded-lg object-cover"
            />
            <span
              v-else
              class="grid size-12 shrink-0 place-items-center rounded-lg bg-n-violet-3 text-n-violet-11"
            >
              <Icon icon="i-lucide-file-text" class="size-5" />
            </span>
            <span class="min-w-0">
              <strong class="block truncate text-sm text-n-slate-12">{{
                attachment.filename
              }}</strong>
              <span class="text-xs text-n-slate-9">Abrir anexo</span>
            </span>
            <Icon
              icon="i-lucide-download"
              class="ml-auto size-4 shrink-0 text-n-slate-9 group-hover:text-n-brand"
            />
          </a>
        </div>
        <div
          class="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-n-weak pt-4"
        >
          <div class="flex gap-4 text-xs text-n-slate-9">
            <span>{{ topic.views_count }} visualizações</span
            ><span>{{ topic.posts_count }} respostas</span>
          </div>
          <div class="flex gap-2">
            <Button
              :label="topic.subscribed ? 'Acompanhando' : 'Acompanhar'"
              :icon="topic.subscribed ? 'i-lucide-bell-ring' : 'i-lucide-bell'"
              color="slate"
              variant="faded"
              size="sm"
              :is-loading="interactionId === 'subscription'"
              @click="toggleSubscription"
            /><Button
              label="Denunciar"
              icon="i-lucide-flag"
              color="slate"
              variant="ghost"
              size="sm"
              @click="openReport('topic', topic)"
            />
          </div>
        </div>
      </article>

      <section>
        <div class="mb-3 flex items-center justify-between gap-3">
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ topic.posts_count }} respostas
          </h2>
          <Select
            v-if="topic.posts.length > 1"
            v-model="replySort"
            :options="replySortOptions"
            class="w-44"
          />
        </div>
        <div
          v-if="!topic.posts.length"
          class="rounded-xl border border-dashed border-n-weak p-10 text-center text-sm text-n-slate-9"
        >
          Seja a primeira pessoa a responder.
        </div>
        <div v-else class="space-y-3">
          <article
            v-for="post in sortedPosts"
            :key="post.id"
            class="rounded-xl border bg-n-solid-1 p-5"
            :class="
              topic.accepted_post_id === post.id
                ? 'border-n-teal-7 ring-1 ring-n-teal-5'
                : 'border-n-weak'
            "
          >
            <div class="flex items-start justify-between gap-3">
              <div class="flex items-center gap-3">
                <img
                  v-if="post.author.avatar_url"
                  :src="post.author.avatar_url"
                  :alt="post.author.name"
                  class="size-9 rounded-full object-cover"
                /><span
                  v-else
                  class="grid size-9 place-items-center rounded-full bg-n-alpha-2 text-sm font-semibold text-n-slate-11"
                  >{{ post.author.name.charAt(0) }}</span
                >
                <div>
                  <p class="text-sm font-semibold text-n-slate-12">
                    {{ post.author.name }}
                    <span
                      v-if="post.author.role !== 'member'"
                      class="ml-1 text-xs text-n-violet-10"
                      >{{ roleLabel(post.author.role) }}</span
                    >
                  </p>
                  <p class="text-xs text-n-slate-9">
                    {{ formatDate(post.created_at)
                    }}<span v-if="post.edited_at"> · editado</span>
                  </p>
                </div>
              </div>
              <span
                v-if="topic.accepted_post_id === post.id"
                class="inline-flex items-center gap-1 rounded-full bg-n-teal-3 px-2 py-1 text-xs font-medium text-n-teal-11"
                ><Icon icon="i-lucide-circle-check" class="size-3.5" />Solução
                aceita</span
              >
            </div>
            <p
              class="mt-4 whitespace-pre-wrap break-words text-sm leading-7 text-n-slate-11"
            >
              {{ post.body }}
            </p>
            <div
              v-if="post.mentions?.length"
              class="mt-3 flex flex-wrap gap-1.5"
            >
              <span
                v-for="user in post.mentions"
                :key="user.id"
                class="rounded-full bg-n-violet-3 px-2.5 py-1 text-xs font-medium text-n-violet-11"
              >
                @{{ user.name }}
              </span>
            </div>
            <div
              v-if="post.attachments?.length"
              class="mt-3 grid gap-2 sm:grid-cols-2"
            >
              <a
                v-for="attachment in post.attachments"
                :key="attachment.id"
                :href="attachment.url"
                class="group flex min-w-0 items-center gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-2.5 hover:bg-n-alpha-2"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon
                  icon="i-lucide-paperclip"
                  class="size-4 shrink-0 text-n-slate-9"
                />
                <span
                  class="min-w-0 flex-1 truncate text-xs font-medium text-n-slate-11"
                  >{{ attachment.filename }}</span
                >
                <Icon
                  icon="i-lucide-download"
                  class="size-4 shrink-0 text-n-slate-9"
                />
              </a>
            </div>
            <div
              class="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-n-weak pt-3"
            >
              <Button
                :label="`${post.liked ? 'Curtido' : 'Curtir'} · ${post.likes_count}`"
                icon="i-lucide-thumbs-up"
                :color="post.liked ? 'blue' : 'slate'"
                :variant="post.liked ? 'solid' : 'ghost'"
                size="xs"
                :is-loading="interactionId === `like-${post.id}`"
                @click="toggleLike(post)"
              />
              <div class="flex gap-1">
                <Button
                  v-if="topic.can_edit && topic.accepted_post_id !== post.id"
                  label="Marcar como solução"
                  icon="i-lucide-circle-check"
                  color="teal"
                  variant="ghost"
                  size="xs"
                  :is-loading="interactionId === `solution-${post.id}`"
                  @click="acceptSolution(post)"
                /><Button
                  label="Denunciar"
                  icon="i-lucide-flag"
                  color="slate"
                  variant="ghost"
                  size="xs"
                  @click="openReport('post', post)"
                />
              </div>
            </div>
          </article>
        </div>
      </section>

      <form
        v-if="topic.status === 'open' && !replyComposerCollapsed"
        ref="replyComposer"
        class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 shadow-sm"
        @submit.prevent="createReply"
        @focusin="replyComposerFocused = true"
        @focusout="handleComposerFocusOut"
      >
        <h2 class="text-base font-semibold text-n-slate-12">
          Adicionar resposta
        </h2>
        <p class="mt-1 text-xs text-n-slate-9">
          Compartilhe uma solução clara e evite publicar dados pessoais ou
          credenciais.
        </p>
        <TextArea
          v-model="replyBody"
          class="mt-4"
          placeholder="Escreva sua resposta..."
          min-height="8rem"
          :max-length="20000"
          show-character-count
          :disabled="!canParticipate"
        />
        <ForumComposerExtras
          v-model:files="replyFiles"
          v-model:mentioned-users="replyMentionedUsers"
          class="mt-3"
          :disabled="!canParticipate || savingReply"
        />
        <div class="mt-3 flex justify-end">
          <Button
            type="submit"
            label="Publicar resposta"
            icon="i-lucide-send"
            :is-loading="savingReply"
            :disabled="
              savingReply || !canParticipate || replyBody.trim().length < 2
            "
          />
        </div>
      </form>
      <button
        v-else-if="topic.status === 'open'"
        type="button"
        class="sticky bottom-4 z-20 flex w-full items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-solid-2 px-4 py-3 text-left shadow-lg transition hover:border-n-brand"
        @click="expandReplyComposer"
      >
        <span class="inline-flex items-center gap-3">
          <span
            class="grid size-9 place-items-center rounded-lg bg-n-brand/10 text-n-brand"
          >
            <Icon icon="i-lucide-message-square-reply" class="size-4" />
          </span>
          <span>
            <strong class="block text-sm text-n-slate-12">
              Adicionar resposta
            </strong>
            <span class="block text-xs text-n-slate-9">
              Clique para abrir o editor
            </span>
          </span>
        </span>
        <Icon icon="i-lucide-chevron-up" class="size-4 text-n-slate-9" />
      </button>
      <div
        v-else
        class="rounded-xl border border-n-amber-6 bg-n-amber-2 p-4 text-sm text-n-amber-11"
      >
        Este tópico está fechado para novas respostas.
      </div>
    </template>

    <Teleport to="body">
      <div
        v-if="reportTarget"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
        @click.self="reportTarget = null"
      >
        <form
          class="w-full max-w-md rounded-2xl border border-n-weak bg-n-solid-1 p-6 shadow-2xl"
          @submit.prevent="submitReport"
        >
          <div class="flex items-start gap-3">
            <span
              class="grid size-10 place-items-center rounded-lg bg-n-amber-3 text-n-amber-10"
            >
              <Icon icon="i-lucide-flag" class="size-5" />
            </span>
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                Denunciar conteúdo
              </h2>
              <p class="mt-1 text-sm text-n-slate-10">
                A equipe de moderação analisará a denúncia. O autor não verá sua
                identidade.
              </p>
            </div>
          </div>
          <div class="mt-5 space-y-4">
            <div>
              <label class="mb-1 block text-sm font-medium text-n-slate-12"
                >Motivo</label
              ><Select
                v-model="reportForm.reason"
                :options="reportReasons"
                class="w-full [&>select]:w-full"
              />
            </div>
            <TextArea
              v-model="reportForm.details"
              label="Detalhes adicionais"
              placeholder="Explique o problema para ajudar na análise."
              :max-length="1000"
              show-character-count
              min-height="6rem"
            />
          </div>
          <div class="mt-5 flex justify-end gap-3">
            <Button
              type="button"
              label="Cancelar"
              color="slate"
              variant="outline"
              @click="reportTarget = null"
            /><Button
              type="submit"
              label="Enviar denúncia"
              color="amber"
              :is-loading="interactionId === 'report'"
              :disabled="interactionId === 'report'"
            />
          </div>
        </form>
      </div>
    </Teleport>
  </main>
</template>
